import { useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/ui/toast';
import { apiCurrentVersion, apiErrorCode } from '@/lib/api';
import { captureFeedbackDeletionCommand, type FeedbackDeletionState } from '@/lib/feedbackDeletion';
import { removeDeletedFeedback } from '@/lib/queryClient';
import type { FeedbackDeletionScope } from '@/lib/types';

export type FeedbackDeleteTarget = {
  feedbackId: string; expectedVersion: number;
  /** Who loses it: every player's conversation, or the coaches-only record. */
  audience: 'players' | 'coaches';
};

const REJECTIONS: Record<string, string> = {
  feedback_creator_required: 'Only the coach who wrote this Feedback can delete it.',
  feedback_published_required: 'This is still a draft. Discard it from the editor instead.',
  feedback_unavailable: 'This Feedback is no longer available. It may already have been deleted.',
};

/** Creator-only delete of one published Feedback for everyone it reached. */
export function DeleteFeedbackDialog({ scope, target, onDeleted, onClose }: {
  scope: FeedbackDeletionScope; target: FeedbackDeleteTarget; onDeleted(): void; onClose(): void;
}) {
  const toast = useToast();
  const capture = (expectedVersion: number) => captureFeedbackDeletionCommand({ actorId: scope.actorId,
    clubId: scope.clubId, teamId: scope.teamId, seasonId: scope.seasonId }, { feedbackId: target.feedbackId, body: {
      schema_version: 'feedback-version.v1', operation_id: crypto.randomUUID(), expected_version: expectedVersion } });
  // One captured operation per confirmation; an unconfirmed send retries it. A
  // newer version is only targeted after the user confirms again (no auto-rebase).
  const [command, setCommand] = useState(() => capture(target.expectedVersion));
  const [rebase, setRebase] = useState<number | null>(null);
  const [state, setState] = useState<FeedbackDeletionState>(() => command.getState());
  const [error, setError] = useState<string | null>(null);
  // The lists and conversation may hold a removed parent or an older version.
  const refresh = () => { void removeDeletedFeedback(scope, target.feedbackId).catch(() => {}); };

  const confirm = async () => {
    setError(null);
    const current = rebase === null ? command : capture(rebase);
    if (rebase !== null) { setCommand(current); setRebase(null); }
    const pending = current.send();
    setState(current.getState());
    try {
      await pending;
      toast.success('Feedback deleted');
      onDeleted();
    } catch (failure) {
      const next = current.getState(), code = apiErrorCode(failure);
      setState(next);
      if (next.status === 'retired' || code === 'feedback_unavailable' || code === 'feedback_version_conflict') refresh();
      const newer = code === 'feedback_version_conflict' ? apiCurrentVersion(failure) : null;
      setRebase(newer);
      setError(next.status === 'unavailable' ? 'Your team or season changed. Close and reopen this Feedback.'
        : next.status === 'retired' ? 'This delete may already have happened. Close and check the list.'
        : next.status === 'unknown' ? 'The delete was not confirmed.'
        : code === 'feedback_version_conflict' ? newer === null
          ? 'This Feedback changed since you opened it. Close and reopen it.'
          : 'This Feedback changed since you opened it. Confirm again to delete the latest version.'
        : REJECTIONS[code ?? ''] ?? 'The Feedback could not be deleted.');
    }
  };
  const sending = state.status === 'sending';
  const settled = rebase === null && (state.status === 'rejected' || state.status === 'retired' || state.status === 'unavailable');

  return <ConfirmDialog title="Delete Feedback?" danger data-testid="feedback-delete-dialog" confirmTestId="feedback-delete-confirm"
    message={<>
      <p>{target.audience === 'players'
        ? 'This deletes the Feedback for everyone, including every player’s conversation and all replies.'
        : 'This deletes the coaches-only Feedback for every coach.'} Goals created from it are kept. This can’t be undone.</p>
      {error && <p role="alert">{error}</p>}
      {state.status === 'unknown' && <p>Keep this tab open. The delete result has not been confirmed.</p>}
    </>}
    confirmLabel={state.status === 'unknown' ? 'Check original operation' : 'Delete Feedback'} confirmDisabled={sending || settled}
    onConfirm={() => void confirm()} onClose={() => { if (!sending) onClose(); }} />;
}
